import { Box } from '@mui/material';
import { HeaderType } from '@components/header/Header.constants';
import { HeaderWrapper } from '@components/headerWrapper/HeaderWrapper';
import { AlertStatus } from '@models/alert';
import { Alert } from '@components/alert/Alert';
import { useQuote } from '@services/quotes';
import { useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { ImpersonationAlertBanner } from '@pages/QuoteDetails/components/ImpersonationAlertBanner';
import { BindQuoteHeaderButtons } from '@pages/BindQuote/BindQuoteHeaderButtons';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { useGetPolicy } from '@services/policy/policies';
import { QuoteType } from '@models/quotes';
import {
  underlyingCoverageBindingRequirementDefaultMessage,
  isUnderlyingCoverageBindingRequirementUnmet,
} from '@pages/QuoteDetails/constants/QuoteDetails.constants';

const GenericBrokeragePrefix = 'Generic Brokerage';

export type BindQuoteHeaderProps = {
  hasUnsavedChanges: boolean;
  canNavigateAway: boolean;
  areBillingFieldsFilled: boolean;
  areDeclarationFieldsFilled: boolean;
  onHandleBindQuote: () => void;
  onShowMultiPolicyBindDialog: (value: boolean) => void;
  onShowRolesManagementDialog: (value: boolean) => void;
  onOpenChangelog: () => void;
  onSaveQuote: ({ exitOnSave }: { exitOnSave?: boolean }) => void;
};

export const BindQuoteHeader = ({
  hasUnsavedChanges,
  canNavigateAway,
  areDeclarationFieldsFilled,
  areBillingFieldsFilled,
  onHandleBindQuote,
  onShowMultiPolicyBindDialog,
  onShowRolesManagementDialog,
  onOpenChangelog,
  onSaveQuote,
}: BindQuoteHeaderProps) => {
  const { isMobileScreen } = useGetScreenSize();
  const { quoteId } = useParams<{ quoteId: string }>();
  invariant(quoteId);
  const { quote } = useQuote(quoteId);

  const { data: policy } = useGetPolicy({ policyId: quote?.policyId });

  if (!quote) {
    return null;
  }

  const showGenericBrokerageAlert = !!quote?.brokerage && quote.brokerage.name.startsWith(GenericBrokeragePrefix);

  const underlyingCoverageBindingRequirement = quote.underlyingCoverageBindingRequirement;
  const underlyingCoverageBindingRequirementUnmet = isUnderlyingCoverageBindingRequirementUnmet(
    underlyingCoverageBindingRequirement,
  );
  const { quoteId: glQuoteId, policyId: glPolicyId } = underlyingCoverageBindingRequirement ?? {};

  const HeaderButtonsComponent = (
    <BindQuoteHeaderButtons
      areBillingFieldsFilled={areBillingFieldsFilled}
      areDeclarationFieldsFilled={areDeclarationFieldsFilled}
      onOpenChangelog={onOpenChangelog}
      onShowMultiPolicyBindDialog={onShowMultiPolicyBindDialog}
      onHandleBindQuote={onHandleBindQuote}
      onSaveQuote={onSaveQuote}
      canNavigateAway={canNavigateAway}
    />
  );
  // mobile renders HeaderButtonsComponent in another component (BindQuotePage) for a better UX
  const headerButtons = isMobileScreen ? [] : [HeaderButtonsComponent];

  let underlyingCoverageNotActive = false;
  let underlyingCoverageExpiresBeforeQuote = false;

  // if relatedPolicySources has an underlying coverage and it is not active or expired, disable bind
  // or it is active or expired but the current quote expires after the associated policy also disable bind
  if (quote.quoteType === QuoteType.Renewal && policy?.relatedPolicySources) {
    const underlyingCoverageRelation = policy.relatedPolicySources.find((relation) => {
      return relation.policyRelationType === 'UnderlyingCoverage';
    });

    if (underlyingCoverageRelation) {
      if (
        underlyingCoverageRelation.sourcePolicy.status !== 'Active' &&
        underlyingCoverageRelation.sourcePolicy.status !== 'Expired'
      ) {
        underlyingCoverageNotActive = true;
      } else {
        if (quote.expiresOn > new Date(underlyingCoverageRelation.sourcePolicy.policyTerm?.expiresOn || '1970-01-01')) {
          underlyingCoverageExpiresBeforeQuote = true;
        }
      }
    }
  }

  return (
    <>
      <HeaderWrapper
        title='Bind Quote'
        type={HeaderType.Detail}
        quote={quote}
        showNavLinks={false}
        hasUnsavedChanges={hasUnsavedChanges}
        buttons={headerButtons}
      />
      <Box sx={sxStyles.box1}>
        <ImpersonationAlertBanner />
        {showGenericBrokerageAlert ? (
          <Alert
            type={AlertStatus.Warning}
            ctaText='Reassign Quote'
            ctaFunction={() => onShowRolesManagementDialog(true)}
            sx={{
              position: 'sticky',
            }}
          >
            This quote is assigned to a generic partner and must be reassigned before bind.
          </Alert>
        ) : null}
        {underlyingCoverageNotActive || underlyingCoverageExpiresBeforeQuote ? (
          <Alert
            type={AlertStatus.Warning}
            sx={{
              position: 'sticky',
            }}
          >
            The Package Renewal must be active and bound before Excess Renewal can be bound.
          </Alert>
        ) : null}
        {underlyingCoverageBindingRequirementUnmet ? (
          <Alert
            type={AlertStatus.Warning}
            ctaText={glQuoteId ? 'View GL Quote' : glPolicyId ? 'View GL Policy' : undefined}
            ctaUrl={glQuoteId ? `/quote/${glQuoteId}` : glPolicyId ? `/policy/${glPolicyId}` : undefined}
            sx={{
              position: 'sticky',
            }}
          >
            {underlyingCoverageBindingRequirement?.reason || underlyingCoverageBindingRequirementDefaultMessage}
          </Alert>
        ) : null}
      </Box>
    </>
  );
};

// moving to an object to help keep flow readable above
const sxStyles = {
  box1: {
    position: 'sticky',
    zIndex: 2,
    minWidth: '100%',
    top: 'unset',
  },
};
